import { useState, useCallback, useEffect } from "react";
import { Wand2, Loader2, Check, X, RefreshCw } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";

const OPERATIONS = [
  { id: "generate", label: "Generate", description: "Create from strategy context", needsValue: false },
  { id: "improve", label: "Improve", description: "Clearer and more impactful", needsValue: true },
  { id: "expand", label: "Expand", description: "Add detail and specificity", needsValue: true },
  { id: "simplify", label: "Simplify", description: "Plain language, less complexity", needsValue: true },
  { id: "rewrite", label: "Rewrite", description: "Full rewrite from scratch", needsValue: true },
];

/**
 * MA-AI-ASSIST-POLISH-088: AI Field Assist
 *
 * Inline AI control with generation state feedback.
 * Shows contextual "Generate" / "Improve" button integrated with field label.
 */
export function AIFieldAssist({ field, currentValue, strategyContext, onAccept }) {
  const [open, setOpen] = useState(false);
  const [state, setState] = useState("idle"); // idle | menu | loading | result | success
  const [result, setResult] = useState("");
  const [alternatives, setAlternatives] = useState([]);
  const [selectedAlt, setSelectedAlt] = useState(-1);
  const [lastOp, setLastOp] = useState(null);

  const hasValue = currentValue && currentValue.trim().length > 0;

  // Button label based on state
  let buttonLabel, ButtonIcon;
  if (state === "loading") {
    buttonLabel = "Generating...";
    ButtonIcon = Loader2;
  } else if (state === "success") {
    buttonLabel = "Generated";
    ButtonIcon = Check;
  } else {
    buttonLabel = hasValue ? "Improve" : "Generate";
    ButtonIcon = Wand2;
  }

  // Success state auto-revert
  useEffect(() => {
    if (state === "success") {
      const t = setTimeout(() => setState("idle"), 1500);
      return () => clearTimeout(t);
    }
  }, [state]);

  const runOperation = useCallback(async (operation) => {
    setState("loading");
    setLastOp(operation);
    try {
      const res = await api.post("/ai/field-operation", {
        field, operation,
        strategy_context: strategyContext,
        current_value: currentValue || "",
        options: {},
      });
      if (res.data?.success) {
        setResult(res.data.result || "");
        setAlternatives(res.data.alternatives || []);
        setSelectedAlt(-1);
        setState("result");
      } else {
        setState("idle");
        setOpen(false);
      }
    } catch {
      setState("idle");
      setOpen(false);
    }
  }, [field, strategyContext, currentValue]);

  const handleAccept = useCallback(() => {
    const value = selectedAlt >= 0 ? alternatives[selectedAlt] : result;
    onAccept(value);
    setOpen(false);
    setResult("");
    setAlternatives([]);
    setState("success");
  }, [result, alternatives, selectedAlt, onAccept]);

  const handleDismiss = useCallback(() => {
    setState("idle");
    setOpen(false);
    setResult("");
    setAlternatives([]);
  }, []);

  const handleRegenerate = useCallback(() => {
    if (lastOp) runOperation(lastOp);
  }, [lastOp, runOperation]);

  const displayValue = selectedAlt >= 0 ? alternatives[selectedAlt] : result;

  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (!o && state !== "success") setState("idle"); }}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={state === "loading"}
          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-medium transition-all disabled:opacity-60 ${
            state === "success"
              ? "text-emerald-400"
              : state === "loading"
                ? "text-indigo-300"
                : "text-indigo-400/80 hover:text-indigo-300 hover:bg-indigo-500/[0.08]"
          }`}
          data-testid={`ai-assist-${field.replace(/\./g, "-")}`}
          title="AI Assist"
        >
          <ButtonIcon className={`w-3 h-3 ${state === "loading" ? "animate-spin" : ""}`} />
          {buttonLabel}
        </button>
      </PopoverTrigger>

      <PopoverContent
        side="right" align="start" sideOffset={8}
        className="w-80 p-0 bg-slate-900 border-slate-700/50 shadow-xl"
        data-testid={`ai-assist-popover-${field.replace(/\./g, "-")}`}
      >
        {state === "loading" && (
          <div className="p-6 flex flex-col items-center gap-3" data-testid="ai-assist-loading">
            <Loader2 className="w-5 h-5 text-indigo-400 animate-spin" />
            <p className="text-xs text-slate-400">Generating...</p>
          </div>
        )}

        {(state === "idle" || state === "menu") && (
          <div className="p-1.5" data-testid="ai-assist-menu">
            <p className="px-2 py-1.5 text-[10px] font-medium text-slate-500 uppercase tracking-wider">AI Operations</p>
            {OPERATIONS.filter((op) => !op.needsValue || hasValue).map((op) => (
              <button key={op.id} type="button" onClick={() => runOperation(op.id)}
                className="w-full flex items-start gap-2.5 px-2.5 py-2 rounded-md text-left hover:bg-slate-800/70 transition-colors group"
                data-testid={`ai-op-${op.id}`}>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-slate-200 group-hover:text-white">{op.label}</p>
                  <p className="text-[10px] text-slate-500">{op.description}</p>
                </div>
              </button>
            ))}
          </div>
        )}

        {state === "result" && (
          <div className="p-3 space-y-3" data-testid="ai-assist-result">
            <div className="space-y-1.5">
              <p className="text-[10px] font-medium text-slate-500 uppercase tracking-wider">
                {selectedAlt >= 0 ? `Alternative ${selectedAlt + 1}` : "Suggestion"}
              </p>
              <div className="p-2.5 rounded-md bg-slate-800/60 border border-slate-700/30">
                <p className="text-xs text-slate-200 leading-relaxed whitespace-pre-wrap">{displayValue}</p>
              </div>
            </div>

            {alternatives.length > 0 && (
              <div className="space-y-1">
                <button type="button" onClick={() => setSelectedAlt(-1)}
                  className={`w-full text-left px-2 py-1 rounded text-[10px] transition-colors ${
                    selectedAlt === -1 ? "bg-indigo-500/20 text-indigo-300 border border-indigo-500/30" : "text-slate-400 hover:bg-slate-800/50"
                  }`}>Primary suggestion</button>
                {alternatives.map((alt, i) => (
                  <button key={i} type="button" onClick={() => setSelectedAlt(i)}
                    className={`w-full text-left px-2 py-1 rounded text-[10px] transition-colors truncate ${
                      selectedAlt === i ? "bg-indigo-500/20 text-indigo-300 border border-indigo-500/30" : "text-slate-400 hover:bg-slate-800/50"
                    }`}>Alt {i + 1}: {alt.substring(0, 60)}{alt.length > 60 ? "..." : ""}</button>
                ))}
              </div>
            )}

            <div className="flex items-center gap-1.5 pt-1 border-t border-slate-700/30">
              <Button size="sm" onClick={handleAccept} className="h-7 px-3 text-[11px] bg-indigo-600 hover:bg-indigo-500 text-white" data-testid="ai-assist-accept">
                <Check className="w-3 h-3 mr-1" /> Accept
              </Button>
              <Button size="sm" variant="ghost" onClick={handleRegenerate} className="h-7 px-2.5 text-[11px] text-slate-400 hover:text-slate-200" data-testid="ai-assist-regenerate">
                <RefreshCw className="w-3 h-3 mr-1" /> Retry
              </Button>
              <Button size="sm" variant="ghost" onClick={handleDismiss} className="h-7 px-2.5 text-[11px] text-slate-500 hover:text-slate-300 ml-auto" data-testid="ai-assist-dismiss">
                <X className="w-3 h-3" />
              </Button>
            </div>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
